import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, InputLabel } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getJobById,
  getSubmissionSettings,
  IExcelImportRequest,
  IJob,
  importRecords,
  ISubmissionSettingsResponse,
} from 'wisestep-api';
import { ControlledTextInput, WsLocationSearch } from 'wisestep-base';

import { WsTagsFilter } from '../../../../components/filters/components';
import { WsSkillsFilter } from '../../../../components/filters/skillsFilter';
import { useTokenContext } from '../../../../context';
import { failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import ModalForRedirection from '../../../emailSettings/components/twoWayEmailSync/ModalForRedirection';
import {
  bulkMappingsUploadSchema,
  bulkSubmissionsUploadSchema,
} from '../../utils/bulkSubmissionsHelper';

import BulkUploadSpreadSheets from './BulkUploadSpreadSheets';
import SpreadSheetSource from './SpreadSheetSource';
import UploadSpreadSheetsHeader from './UploadSpreadSheetsHeader';

interface IUploadFromSpreadSheetsProps {
  uploadingFor?: 'submissions' | 'mapping';
  job?: IJob;
  onClose?: (openDialog: boolean) => void;
}
const UploadFromSpreadSheets = ({
  uploadingFor = 'mapping',
  job,
  onClose,
}: IUploadFromSpreadSheetsProps) => {
  const [submissionSetting, setSubmissionSettings] = useState<ISubmissionSettingsResponse>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  // const [openHistoryDrawer, setOpenHistoryDrawer] = useState(false);
  const [openMappingDialog, setOpenMappingDialog] = useState<boolean>(false);
  const token = useTokenContext();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [params] = useSearchParams();
  const jobId = String(params.get('jobId') ?? '');
  const navigate = useNavigate();

  useEffect(() => {
    const getSubmissionsSettings = async () => {
      const setting = await getSubmissionSettings();
      setSubmissionSettings(setting.data);
    };
    uploadingFor == 'submissions' && getSubmissionsSettings();
  }, [uploadingFor]);

  const methods = useForm<IExcelImportRequest>({
    resolver: yupResolver(
      uploadingFor == 'mapping' ? bulkMappingsUploadSchema : bulkSubmissionsUploadSchema
    ),
  });

  const { handleSubmit, reset, setValue, getValues } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);

  useEffect(() => {
    const getJob = async () => {
      if (jobId) {
        const job = await getJobById(jobId);
        setValue('jobTitle', job.data.title);
        setValue('jobId', jobId);
        setValue('jobCode', job.data.jobCode);
      }
    };
    getJob();
  }, [jobId, setValue]);

  const onSubmit = async (data: IExcelImportRequest) => {
    try {
      if (openMappingDialog) {
        return;
      }
      if (!isEmpty(job)) {
        data.jobId = job?.id ?? '';
        data.jobTitle = job?.title;
        data.jobCode = job?.jobCode;
      }
      data.ignoreFirstRow = data.ignoreFirstRow ?? true;
      const fieldMappings = [...data.fieldMappings] ?? getValues('fieldMappings');
      data.countryCode = data.locations?.countryCode;
      data.fieldMappings = data.fieldMappings.map((data) => (data ? data : ''));
      data.jobCandidateMapping = uploadingFor === 'mapping';
      await importRecords(data);
      reset({
        skills: [],
        tags: [],
        fieldMappings: fieldMappings,
      });
      setIsSubmitted(true);
      uploadingFor === 'mapping' && setShowModal(true);
      onClose && onClose(true);
    } catch {
      setNotification(failed('Failed to import records , Please try again'));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Box bgcolor="white" minHeight="800px" component={'form'} onSubmit={handleSubmit(onSubmit)}>
          <UploadSpreadSheetsHeader job={job} onClose={() => onClose && onClose(false)} />
          <Grid container rowSpacing={2} columnSpacing={2} p={4} display="flex">
            <Grid item xs={6}>
              <WsLocationSearch
                size="small"
                placeHolder="Country"
                name="locations"
                variant="outlined"
                multiSelect={false}
                showMandatorySymbol={true}
                wisestepApi={true}
                token={token.token}
                authorization={token.auth}
              />
            </Grid>
            <Grid item xs={6}>
              <WsSkillsFilter name="skills" label="Skills" placeHolder="Type to Select Skills" />
            </Grid>
            <Grid item xs={6}>
              <WsTagsFilter placeHolder="Add a tag" name="tags" label="Tags" />
            </Grid>
            <Grid item xs={6}>
              {uploadingFor === 'mapping' && (
                <>
                  <InputLabel sx={{ color: 'black' }}>Source</InputLabel>
                  <SpreadSheetSource name="source" />
                </>
              )}
            </Grid>
            <Grid item xs={6}>
              <ControlledTextInput
                name="jobTitle"
                labelText="Job"
                fullWidth
                id="jobId"
                variant="outlined"
                disabled={true}
              />
            </Grid>
            <Grid item xs={12}>
              <BulkUploadSpreadSheets
                isSubmitted={isSubmitted}
                uploadingFor={uploadingFor}
                submissionSetting={submissionSetting}
                openMappingDialog={openMappingDialog}
                setOpenMappingDialog={setOpenMappingDialog}
                getFieldMappingValues={(columns, ignoreFirstRow) => {
                  setValue('fieldMappings', columns);
                  setValue('ignoreFirstRow', ignoreFirstRow);
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
      {showModal && (
        <ModalForRedirection
          modalState={showModal}
          onClose={() => setShowModal(false)}
          leftButtonText="Skip"
          rightButtonText="View History"
          rightButtonAction={() =>
            navigate('/import-candidates/bulk-upload-excel/candidate-upload-history')
          }
          message="It may take few minutes to process the uploaded candidates, we will notify you once the candidates are processed."
        />
      )}
      {/* {openHistoryDrawer && (
        <ImportHistoryDrawer
          openDrawer={openHistoryDrawer}
          onClose={() => setOpenHistoryDrawer(false)}
        />
      )} */}
    </>
  );
};

export default UploadFromSpreadSheets;
